import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PersonalDetails from './PersonalDetails';
import AppearanceDetails from './AppearanceDetails';
import ContactInformation from './ContactInformation';
import EducationInformation from './EducationInformation';
import FamilyInformation from './FamilyInformation';
import LifePartnerPreferences from './LifePartnerPreferences';
import { registerAPI } from '../services/api';

const MultiStepForm = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState(null);
    const [formData, setFormData] = useState({
        // Personal Details
        name: '',
        gender: '',
        dob: '',
        birth_time: '',
        caste: '',
        horscope: '',
        birth_location: '',
        gotra: '',
        marital: '',
        children: '',
        children_status: '',
        eat_status: '',
        moms_surname: '',
        about_me: '',
        image: null,
        amount: '4000',
        
        // Appearance Details
        height: '',
        weight: '',
        skin_tone: '',
        
        // Contact Information
        country: '',
        state: '',
        district: '',
        city: '',
        residence_status: '',
        email: '',
        contact: '',
        
        // Education Information
        education: '',
        occupation: '',
        monthaly_income: '',
        annual_income: '',
        employee_in: '',
        designation: '',
        
        // Family Information
        father: '',
        father_occupation: '',
        mother: '',
        mother_occupation: '',
        unmarride_sister: '',
        marride_sister: '',
        unmarride_brother: '',
        marride_brother: '',
        
        // Partner Preferences
        looking_for: '',
        marit: '',
        age: '',
        partner_education: '',
        partner_occupation: '',
        location: ''
    });
    
    // Load saved data from session storage
    useEffect(() => {
        const savedData = sessionStorage.getItem('registrationData');
        if (savedData) {
            const parsedData = JSON.parse(savedData);
            setFormData(parsedData);
        }
        
        const savedUserId = sessionStorage.getItem('userId');
        if (savedUserId) {
            setUserId(savedUserId);
        }
        
        const savedStep = sessionStorage.getItem('currentStep');
        if (savedStep) {
            setCurrentStep(parseInt(savedStep));
        }
    }, []);
    
    // Save to session storage
    useEffect(() => {
        if (Object.keys(formData).length > 0) {
            sessionStorage.setItem('registrationData', JSON.stringify(formData));
        }
        if (userId) {
            sessionStorage.setItem('userId', userId);
        }
        sessionStorage.setItem('currentStep', currentStep);
    }, [formData, userId, currentStep]);
    
    const handlePersonalSubmit = async (data) => {
        setLoading(true);
        
        const formDataToSend = new FormData();
        
        Object.keys(data).forEach(key => {
            if (key === 'image' && data[key]) {
                formDataToSend.append(key, data[key]);
            } else if (data[key] !== null && data[key] !== '' && 
                      key !== 'height' && key !== 'weight' && key !== 'skin_tone' &&
                      key !== 'country' && key !== 'state' && key !== 'district' &&
                      key !== 'city' && key !== 'residence_status' && key !== 'email' && 
                      key !== 'contact' && key !== 'education' && key !== 'occupation' &&
                      key !== 'monthaly_income' && key !== 'annual_income' && 
                      key !== 'employee_in' && key !== 'designation' &&
                      key !== 'father' && key !== 'father_occupation' && 
                      key !== 'mother' && key !== 'mother_occupation' &&
                      key !== 'unmarride_sister' && key !== 'marride_sister' &&
                      key !== 'unmarride_brother' && key !== 'marride_brother' &&
                      key !== 'looking_for' && key !== 'marit' && key !== 'age' &&
                      key !== 'partner_education' && key !== 'partner_occupation' && 
                      key !== 'location') {
                formDataToSend.append(key, data[key]);
            }
        });
        
        if (!data.amount) {
            formDataToSend.append('amount', '4000');
        }
        
        try {
            const response = await registerAPI.savePersonalDetails(formDataToSend);
            
            if (response.data.success) {
                setUserId(response.data.userId);
                toast.success('Personal details saved successfully!');
                setCurrentStep(2);
                const updatedData = { ...formData, ...data };
                setFormData(updatedData);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.message || 'Error saving personal details');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAppearanceSubmit = async (data) => {
        setLoading(true);
        
        const submissionData = {
            user_id: userId,
            height: data.height ? Number(data.height) : null,
            weight: data.weight ? Number(data.weight) : null,
            skin_tone: data.skin_tone || null
        };
        
        try {
            const response = await registerAPI.saveAppearanceDetails(submissionData);
            
            if (response.data.success) {
                toast.success('Appearance details saved successfully!');
                setCurrentStep(3);
                const updatedData = { ...formData, ...data };
                setFormData(updatedData);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.message || 'Error saving appearance details');
        } finally {
            setLoading(false);
        }
    };
    
    const handleContactSubmit = async (data) => {
        setLoading(true);
        
        try {
            const response = await registerAPI.saveContactDetails(data);
            
            if (response.data.success) {
                toast.success('Contact details saved successfully!');
                setCurrentStep(4);
                const updatedData = { ...formData, ...data };
                setFormData(updatedData);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.message || 'Error saving contact details');
        } finally {
            setLoading(false);
        }
    };
    
    const handleEducationSubmit = async (data) => {
        setLoading(true);
        
        try {
            const response = await registerAPI.saveEducationDetails(data);
            
            if (response.data.success) {
                toast.success('Education details saved successfully!');
                setCurrentStep(5);
                const updatedData = { ...formData, ...data };
                setFormData(updatedData);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.message || 'Error saving education details');
        } finally {
            setLoading(false);
        }
    };
    
    const handleFamilySubmit = async (data) => {
        setLoading(true);
        
        try {
            const response = await registerAPI.saveFamilyDetails(data);
            
            if (response.data.success) {
                toast.success('Family details saved successfully!');
                setCurrentStep(6);
                const updatedData = { ...formData, ...data };
                setFormData(updatedData);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error(error.response?.data?.message || 'Error saving family details');
        } finally {
            setLoading(false);
        }
    };
 const handlePartnerSubmit = async (data) => {
    setLoading(true);
    
    try {
        const response = await registerAPI.saveLifePartner(data);
        
        if (response.data.success) {
            toast.success('Partner preferences saved successfully! 🎉');
            
            // Get the complete formData which includes the name from PersonalDetails
            const completeFormData = {
                ...formData,  // This has the name from step 1
                ...data       // Add partner preferences
            };
            
            console.log('Name being sent to payment:', completeFormData.name); // Debug
            
            // Navigate to payment method page with state
            navigate('/payment-method', { 
                state: { 
                    userId: userId, 
                    amount: 4000,
                    formData: {
                        name: completeFormData.name,  // Explicitly pass the name
                        email: completeFormData.email,
                        contact: completeFormData.contact,
                        show_id: userId
                    }
                } 
            });
            
            // Clear session storage after navigation
            setTimeout(() => {
                sessionStorage.removeItem('registrationData');
                sessionStorage.removeItem('userId');
                sessionStorage.removeItem('currentStep');
            }, 1000);
        }
    } catch (error) {
        console.error('Error:', error);
        toast.error(error.response?.data?.message || 'Error saving partner preferences');
    } finally {
        setLoading(false);
    }
};
    const handlePrevious = (step) => {
        setCurrentStep(step);
    };
    
    return (
        <div className="min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Progress Steps */}
                <div className="mb-8">
                    <div className="flex justify-between items-center">
                        <div className="text-center flex-1">
                            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-semibold ${currentStep >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                                1
                            </div>
                            <p className="text-xs mt-1">Personal</p>
                        </div>
                        <div className="flex-1 h-1 bg-gray-300">
                            <div className={`h-full bg-indigo-600 transition-all ${currentStep >= 2 ? 'w-full' : 'w-0'}`}></div>
                        </div>
                        <div className="text-center flex-1">
                            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-semibold ${currentStep >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                                2
                            </div>
                            <p className="text-xs mt-1">Appearance</p>
                        </div>
                        <div className="flex-1 h-1 bg-gray-300">
                            <div className={`h-full bg-indigo-600 transition-all ${currentStep >= 3 ? 'w-full' : 'w-0'}`}></div>
                        </div>
                        <div className="text-center flex-1">
                            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-semibold ${currentStep >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                                3
                            </div>
                            <p className="text-xs mt-1">Contact</p>
                        </div>
                        <div className="flex-1 h-1 bg-gray-300">
                            <div className={`h-full bg-indigo-600 transition-all ${currentStep >= 4 ? 'w-full' : 'w-0'}`}></div>
                        </div>
                        <div className="text-center flex-1">
                            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-semibold ${currentStep >= 4 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                                4
                            </div>
                            <p className="text-xs mt-1">Career</p>
                        </div>
                        <div className="flex-1 h-1 bg-gray-300">
                            <div className={`h-full bg-indigo-600 transition-all ${currentStep >= 5 ? 'w-full' : 'w-0'}`}></div>
                        </div>
                        <div className="text-center flex-1">
                            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-semibold ${currentStep >= 5 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>
                                5
                            </div>
                            <p className="text-xs mt-1">Family</p>
                        </div>
                        <div className="flex-1 h-1 bg-gray-300">
                            <div className={`h-full bg-indigo-600 transition-all ${currentStep >= 6 ? 'w-full' : 'w-0'}`}></div>
                        </div>
                        <div className="text-center flex-1">
                            <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-semibold ${currentStep >= 6 ? 'bg-pink-600 text-white' : 'bg-gray-300'}`}>
                                6
                            </div>
                            <p className="text-xs mt-1">Partner</p>
                        </div>
                    </div>
                </div>
                
                {/* Form Cards */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="p-6 md:p-8">
                        {currentStep === 1 && (
                            <PersonalDetails 
                                formData={formData}
                                onSubmit={handlePersonalSubmit}
                                loading={loading}
                                updateFormData={setFormData}
                            />
                        )}
                        
                        {currentStep === 2 && (
                            <AppearanceDetails 
                                formData={formData}
                                onSubmit={handleAppearanceSubmit}
                                onPrevious={() => handlePrevious(1)}
                                loading={loading}
                            />
                        )}
                        
                        {currentStep === 3 && (
                            <ContactInformation 
                                formData={formData}
                                onSubmit={handleContactSubmit}
                                onPrevious={() => handlePrevious(2)}
                                loading={loading}
                                userId={userId}
                            />
                        )}
                        
                        {currentStep === 4 && (
                            <EducationInformation 
                                formData={formData}
                                onSubmit={handleEducationSubmit}
                                onPrevious={() => handlePrevious(3)}
                                loading={loading}
                                userId={userId}
                            />
                        )}
                        
                        {currentStep === 5 && (
                            <FamilyInformation 
                                formData={formData}
                                onSubmit={handleFamilySubmit}
                                onPrevious={() => handlePrevious(4)}
                                loading={loading}
                                userId={userId}
                            />
                        )}
                        
                        {currentStep === 6 && (
                            <LifePartnerPreferences 
                                formData={formData}
                                onSubmit={handlePartnerSubmit}
                                onPrevious={() => handlePrevious(5)}
                                loading={loading}
                                userId={userId}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiStepForm;